import { MDBBadge, MDBBtn, MDBBtnGroup, MDBCard, MDBCardBody, MDBCardHeader, MDBCol, MDBInput, MDBInputGroup, MDBModal, MDBModalBody, MDBModalContent, MDBModalDialog, MDBModalFooter, MDBModalHeader, MDBRow, MDBTable, MDBTableBody, MDBTableHead } from "mdb-react-ui-kit";
import React from "react";
import useLocale from "../../util/i18n";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faAngleLeft, faAngleRight, faSpinner, faXmark } from "@fortawesome/free-solid-svg-icons";
import { Helmet } from "react-helmet-async";
import { useCrumbs } from "../../component/breadcrumbs";
import { getCountHistory, getStock, getStockConfig, saveCountResult, type StockCount, type StockData, type GridConfig, stockIn, stockOut } from "../../api/stock";
import { assertNumber } from "../../util/misc";
import Toast from "../../component/toast";
import { getConfig } from "../../api/core";
import { debounce } from "lodash";
import ExcelJS from "exceljs";
import { format } from "date-fns";
import Select, { type OptionType } from "../../component/select";
import { adjustStock, getProductGroups, getProductSubGroups, getSalesOrder, getSalesOrderItem, getStockQty, SkuQty } from "../../api/erp";
import { useSearchParams } from "react-router-dom";
import jsPDF from "jspdf";
import html2canvas from "html2canvas-pro";
import BigNumber from "bignumber.js";
import request from "../../util/erp/unleashed";
import { enc, MD5 } from "crypto-js";
import { getProducts } from "../../api/erp";

const ConfigContext = React.createContext<Array<GridConfig>>([]);
const HistoryContext = React.createContext<Array<StockCount>>([]);
const HistoryPageContext = React.createContext<[number, React.Dispatch<React.SetStateAction<number>>, number]>([1, () => { }, 1]);
const OrderContext = React.createContext<[Array<string>, React.Dispatch<React.SetStateAction<string[]>>]>([[], () => { }]);

export default function Inventory() {
    const { 0: name, 1: setName } = React.useState('');
    const { 0: history, 1: setHistory } = React.useState<Array<StockCount>>([]);
    const { 0: selected, 1: setSelected } = React.useState<Array<number>>([]);
    const { 0: racks, 1: setRacks } = React.useState<Array<GridConfig>>([]);
    const { 0: orders, 1: setOrders } = React.useState<Array<string>>([]);
    const { 0: page, 1: setPage } = React.useState(1);
    const { 0: total, 1: setTotal } = React.useState(1);
    const { 0: __ } = useLocale();
    const { 0: setCrumbs } = useCrumbs();
    const { 0: search, 1: setSearch } = useSearchParams();
    const select = React.useCallback((i: number, e: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => {
        e.stopPropagation();
        e.preventDefault();
        setSelected(old => {
            const cloned = [...old];
            const p = cloned.indexOf(i);
            if (p > -1) {
                cloned.splice(p, 1);
            } else {
                cloned.push(i);
            }
            cloned.sort();
            return cloned;
        });
    }, [setSelected]);
    const start = React.useCallback(() => {
        const search = new URLSearchParams();
        for (const id of selected) {
            search.append('rack[]', id.toString());
        }
        setSearch(search);
    }, [selected, setSearch]);
    React.useEffect(() => {
        let crumbs: any = ['Stock Count'];
        if (search.has('rack[]')) {
            crumbs = [
                { link: '/bin/inventory', label: 'Stock Count' },
                search.getAll('rack[]').map(id => racks[parseInt(id)]?.name ?? 'Rack ' + (parseInt(id) + 1)).join('/')
            ];
        } else if (search.has('id')) {
            crumbs = [
                { link: '/bin/inventory', label: 'Stock Count' },
                getID(search.get('id')!)
            ];
        }
        setCrumbs(crumbs);
    }, [setCrumbs, search, racks]);
    React.useEffect(() => {
        getStockConfig().then(response => {
            if (typeof response !== 'string') {
                setRacks(response);
            }
        });
    }, [setRacks]);
    React.useEffect(() => {
        if (search.has('reload')) {
            for (let i = 1; i < Math.ceil(total / 5); i++) {
                sessionStorage.removeItem('sc-' + i);
            }
        }
        const cache = sessionStorage.getItem('sc-' + page);
        if (cache) {
            const { data, total } = JSON.parse(cache);
            setHistory(data);
            setTotal(total);
        } else {
            getCountHistory({ page, limit: 5 }).then(response => {
                if (typeof response !== 'string') {
                    setHistory(response.data);
                    setTotal(response.total);
                    sessionStorage.setItem('sc-' + page, JSON.stringify(response));
                }
            });
            setSelected([]);
        }
    }, [setHistory, search, setSelected, page, setTotal, total]);
    React.useEffect(() => {
        if (search.has('id')) {
            const item = history.find(v => v.id.toString() === search.get('id'));
            if (item) {
                setOrders(item.orders ?? []);
                setName(item.lead);
            }
        } else if (search.has('rack[]')) {
            const rack = search.getAll('rack[]').map(i => parseInt(i));
            setSelected(rack);
            const info = JSON.parse(localStorage.getItem('stock_count_info') || '{}');
            if (info?.lead) {
                setName(info.lead);
                localStorage.setItem('stock_count_info', JSON.stringify({ lead: info.lead, rack }));
            }
        }
    }, [search, history, setName, setOrders, setSelected]);
    return (
        <ConfigContext.Provider value={racks}>
            <HistoryPageContext.Provider value={[page, setPage, total]}>
                <HistoryContext.Provider value={history}>
                    <OrderContext.Provider value={[orders, setOrders]}>
                        <Helmet>
                            <title>{__('Stock Count')}</title>
                        </Helmet>
                        {search.has('id') ? (
                            <Processing />
                        ) : (!name ? (<Start setName={setName} />) : (search.has('rack[]') ? (
                            <Processing lead={name} />
                        ) : (
                            <>
                                <ul className="racks row">
                                    {racks.map((item, i) => (
                                        <li key={(item.name ?? '') + i} className="col-12 col-md-6 col-xl-4 col-xxl-3">
                                            <div className="rack">
                                                <h3 className="name">{item.name ?? __('Area %d').replace('%d', (i + 1).toString())}</h3>
                                                <p className="size">{item.rows + 'x' + item.cols}</p>
                                                <MDBBtn disabled={!selected.includes(i) && selected.length === 2} onClick={select.bind(null, i)} className="view px-1">{__(selected.includes(i) ? 'Cancel' : 'Select')}</MDBBtn>
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                                <MDBCard>
                                    <MDBCardBody className="d-flex justify-content-between">
                                        <MDBBtn color="secondary" onClick={setName.bind(null, '')}>{__('Back')}</MDBBtn>
                                        <MDBBtn onClick={start}>{__('Start')}</MDBBtn>
                                    </MDBCardBody>
                                </MDBCard>
                            </>
                        )))}
                    </OrderContext.Provider>
                </HistoryContext.Provider>
            </HistoryPageContext.Provider>
        </ConfigContext.Provider>
    );
}

function Start(props: { setName: (name: string) => void }) {
    const { 0: __, 1: locale } = useLocale();
    const { 0: name, 1: setName } = React.useState('');
    const { 0: page, 1: setPage, 2: total } = React.useContext(HistoryPageContext);
    const data = React.useContext(HistoryContext);
    const { 1: setSearch } = useSearchParams();
    const { 0: confirm, 1: setConfirm } = React.useState<Array<boolean>>(Array(5).fill(false));
    const config = React.useContext(ConfigContext);
    const formatter = React.useMemo(() => new Intl.DateTimeFormat(locale, { dateStyle: 'medium' }), [locale])
    const onCheck = React.useCallback((i: number) => {
        setConfirm(old => {
            const cloned = [...old];
            cloned[i] = !cloned[i];
            return cloned;
        });
    }, [setConfirm]);
    const onChange = React.useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        setName(e.target.value);
    }, [setName]);
    const download = React.useCallback(async (item: StockCount, e: React.MouseEvent<HTMLElement>) => {
        e.stopPropagation();
        e.preventDefault();
        const data = [];
        let skus = [];
        for (const i of item.result) {
            if (typeof i.diff === 'undefined') {
                continue;
            }
            data.push(i);
            i.newSku && skus.push(i.newSku);
        }
        skus = await filterSku(skus, item.filter);
        getReport(item.id, config, __, data, item.filter?.group?.length ? skus : null, await getOrderedItems(item.orders));
    }, [config, __]);
    const resume = React.useCallback(debounce(() => {
        let result: Array<StockData> = [];
        let left: any = localStorage.getItem('stock_count_left');
        if (left) {
            left = JSON.parse(left);
            for (const i in left ?? {}) {
                result = result.concat(left![i]);
            }
            let right: any = localStorage.getItem('stock_count_right');
            if (right) {
                right = JSON.parse(right);
                for (const i in right ?? {}) {
                    result = result.concat(right![i]);
                }
            }
            const info = JSON.parse(localStorage.getItem('stock_count_info') || '{}');
            if (window.confirm(__('Unsaved data detected. Resume stock count?'))) {
                if (info.id) {
                    setSearch(new URLSearchParams([['id', info.id.toString()]]));
                } else if (info.rack?.length) {
                    const search = [];
                    for (const area of info.rack) {
                        search.push(['rack[]', area.toString()]);
                    }
                    setSearch(new URLSearchParams(search));
                }
            } else {
                localStorage.removeItem('stock_count_left');
                localStorage.removeItem('stock_count_right');
                localStorage.removeItem('stock_count_info');
            }
        }
    }, 600), [setSearch, __]);
    React.useEffect(resume, [resume]);
    return (
        <MDBRow>
            <MDBCol>
                <MDBCard>
                    <MDBCardHeader>
                        <h5 className="mb-0">{__('Start A New Stock Count')}</h5>
                    </MDBCardHeader>
                    <MDBCardBody>
                        <div>
                            <label className="d-block mb-2">
                                <span>1.</span>
                                <input type="checkbox" className="mx-2" checked={confirm[0]} onChange={onCheck.bind(null, 0)} />
                                <span className="label">{__('All orders already in trailer or shipped have been completed')}</span>
                            </label>
                            <label className="d-block mb-2">
                                <span>2.</span>
                                <input type="checkbox" className="mx-2" checked={confirm[1]} onChange={onCheck.bind(null, 1)} />
                                <span className="label">{__('Orders in transit within the warehouse must be either fully stocked or not yet started with picking')}</span>
                            </label>
                            <label className="d-block mb-2">
                                <span>3.</span>
                                <input type="checkbox" className="mx-2" checked={confirm[2]} onChange={onCheck.bind(null, 2)} />
                                <span className="label">{__('All products previously unloaded from the containers have been fully loaded or are in the designated Picking Area, not on the warehouse floor where stacking is prohibited')}</span>
                            </label>
                            <label className="d-block mb-2">
                                <span>4.</span>
                                <input type="checkbox" className="mx-2" checked={confirm[3]} onChange={onCheck.bind(null, 3)} />
                                <span className="label">{__('The Swing-Reach forklift has completed the inspection of all shelves, with all accessory products placed in the designated areas, and no accessory products remaining in the main warehouse zone')}</span>
                            </label>
                            <label className="d-block mb-3">
                                <span>5.</span>
                                <input type="checkbox" className="mx-2" checked={confirm[4]} onChange={onCheck.bind(null, 4)} />
                                <span className="label">{__('Before the inventory check is completed, the warehouse cannot start picking new orders or shipping')}</span>
                            </label>
                        </div>
                        <div className="d-flex justify-content-end gap-3">
                            <label className="btn btn-secondary d-flex gap-2 px-3 align-items-center">
                                <span className="label">{__('Lead By')}</span>
                                <input type="text" value={name} onChange={onChange} />
                            </label>
                            <MDBBtn type="button" disabled={!name || confirm.filter(v => !v).length > 0} onClick={props.setName.bind(null, name)}>{__('Create Stock Count')}</MDBBtn>
                        </div>
                    </MDBCardBody>
                </MDBCard>
            </MDBCol>
            <MDBCol size="12" md="5" lg="4" xxl="3" className="mt-3 mt-md-0">
                <MDBCard>
                    <MDBCardHeader className="d-flex justify-content-between align-items-center">
                        <h5 className="mb-0">{__('Stock Count List')}</h5>
                        <MDBBtnGroup>
                            <MDBBtn size="sm" className="px-2 py-1" disabled={page === 1} color="light" onClick={setPage.bind(null, page - 1)}>
                                <FontAwesomeIcon icon={faAngleLeft} />
                            </MDBBtn>
                            <MDBBtn size="sm" className="px-2 py-1" disabled={page === Math.ceil(total / 5)} color="light" onClick={setPage.bind(null, page + 1)}>
                                <FontAwesomeIcon icon={faAngleRight} />
                            </MDBBtn>
                        </MDBBtnGroup>
                    </MDBCardHeader>
                    <MDBCardBody>
                        {data === null ? (
                            <FontAwesomeIcon icon={faSpinner} spin size="2x" className="d-block mx-auto" />
                        ) : (
                            <ul className="count-list">
                                {data.map(item => (
                                    <li key={item.id}>
                                        <span className="date">{formatter.format((item.updated_at || item.created_at) * 1000)}</span>
                                        <span className="lead">{item.lead}</span>
                                        {item.status == 0 ? (
                                            <MDBBtn size="sm" onClick={setSearch.bind(null, new URLSearchParams([['id', item.id.toString()]]))}>{__('Continue')}</MDBBtn>
                                        ) : (
                                            <MDBBtn size="sm" onClick={download.bind(null, item)}>{__('Report')}</MDBBtn>
                                        )}
                                    </li>
                                ))}
                            </ul>
                        )}
                    </MDBCardBody>
                </MDBCard>
            </MDBCol>
        </MDBRow>
    );
}

const Context = React.createContext<[
    Record<string, Array<StockData>> | null,
    React.Dispatch<React.SetStateAction<Record<string, Array<StockData>> | null>>,
    Record<string, Array<StockData>> | null,
    React.Dispatch<React.SetStateAction<Record<string, Array<StockData>> | null>>
]>([null, () => { }, null, () => { }]);

function Processing(props: { lead?: string }) {
    const { 0: left, 1: setLeft } = React.useState<Record<string, Array<StockData>> | null>(null);
    const { 0: right, 1: setRight } = React.useState<Record<string, Array<StockData>> | null>(null);
    const { 0: asc, 1: setAsc } = React.useState(true);
    const { 0: complete, 1: setComplete } = React.useState(false);
    const { 0: search, 1: setSearch } = useSearchParams();
    const history = React.useContext(HistoryContext);
    const { 0: __ } = useLocale();
    const id = React.useRef(0);
    const lead = React.useRef('');
    const { 0: rack, 1: setRack } = React.useState<Array<number>>([]);
    const config = React.useContext(ConfigContext);
    const { 0: order, 1: setOrder } = React.useState(false);
    const { 0: orders } = React.useContext(OrderContext);
    const columns = React.useMemo(() => {
        const results: number[][] = [];
        for (const i of rack) {
            const result = [];
            if (asc) {
                for (let j = 0; j < (config[i]?.cols ?? 0); j += 3) {
                    result.push(j);
                }
            } else {
                for (let j = (config[i]?.cols ?? 0) - 1; j >= 0; j -= 3) {
                    result.push(j);
                }
            }
            results.push(result);
        }
        return results;
    }, [config, rack, asc]);
    const parseStockData = React.useCallback(async (arr: Array<StockData>) => {
        const data: Record<string, Record<string, StockData>> = {};
        const stock: Record<string, SkuQty> = {};
        const skus = new Set<string>();
        const qty: Record<string, number> = {};
        for (const i of arr) {
            const key = i.row + 'x' + i.col;
            data[key] ??= {};
            i.qty = assertNumber(i.qty);
            if (data[key][i.sku]) {
                data[key][i.sku].qty += i.qty;
            } else {
                data[key][i.sku] = i;
            }
            if (config[i.area]?.picking?.includes(assertNumber(i.row))) {
                skus.add(i.sku);
            } else {
                qty[i.sku] ??= 0;
                qty[i.sku] += assertNumber(i.qty);
            }
        }
        if (skus.size > 0) {
            const sku = Array.from(skus);
            sku.sort();
            const key = 'sq' + MD5(sku.join('\x1F')).toString(enc.Hex);
            const cache = sessionStorage.getItem(key);
            let data: Array<SkuQty> = [];
            if (cache) {
                data = JSON.parse(cache);
            } else {
                const response = await getStockQty({ filter: { sku } });
                if (typeof response !== 'string') {
                    data = response.data;
                    try {
                        sessionStorage.setItem(key, JSON.stringify(data));
                    } catch { }
                }
            }
            for (let item of data) {
                item.aq = assertNumber(item.aq);
                item.qty = assertNumber(item.qty);
                stock[item.sku] = item;
            }
        }
        const result: Record<string, Array<StockData>> = {};
        for (const key in data) {
            const items = Object.values(data[key]);
            if (items.length > 0 && config[items[0].area]?.picking?.includes(assertNumber(items[0].row))) {
                for (let i = 0; i < items.length; i++) {
                    items[i].qty = (stock[items[i].sku]?.aq ?? 0) - (qty[items[i].sku] ?? 0)
                }
            }
            result[key] = items;
        }
        return result;
    }, [config]);
    const loading = React.useRef<HTMLDialogElement>(null);
    const load = React.useCallback((area: number, setData: (data: Record<string, Array<StockData>>) => void) => {
        loading.current?.showModal();
        getStock({ area }).then(async response => {
            if (typeof response !== 'string') {
                setData(await parseStockData(response.data || []));
            }
            loading.current?.close();
        }, () => loading.current?.close());
    }, [parseStockData]);
    const parent = React.useRef<HTMLDivElement>(null);
    const pdf = React.useRef(async (e: React.MouseEvent<HTMLElement>) => {
        e.stopPropagation();
        e.preventDefault();
        loading.current?.showModal();
        const pdf = new jsPDF({ orientation: "l", unit: "mm", format: "a4" });
        const racks = parent.current?.querySelectorAll<HTMLDivElement>('.partial') ?? [];
        const left: HTMLDivElement[] = [], right: HTMLDivElement[] = [];
        for (const rack of racks) {
            rack.classList.add('print');
            const style = window.getComputedStyle(rack);
            if (style.gridColumnStart === '1') {
                left.push(rack);
            } else {
                right.push(rack);
            }
        }
        for (let i = 0; i < left.length || i < right.length; i++) {
            const ps = [];
            if (left[i]) {
                ps.push((async () => {
                    const canvas = await html2canvas(left[i], {
                        scale: 2, useCORS: true, logging: false,
                        backgroundColor: "#ffffff"
                    });
                    pdf.addImage(canvas.toDataURL("image/png"), 'png', 10, 0, 123, (canvas.height * 123) / canvas.width);
                    left[i].classList.remove('print');
                })());
            }
            if (right[i]) {
                ps.push((async () => {
                    const canvas = await html2canvas(right[i], {
                        scale: 2, useCORS: true, logging: false,
                        backgroundColor: "#ffffff"
                    });
                    pdf.addImage(canvas.toDataURL("image/png"), 'png', 164, 0, 123, (canvas.height * 123) / canvas.width);
                    right[i].classList.remove('print');
                })());
            }
            await Promise.all(ps);
            if (i + 1 < left.length || i + 1 < right.length) {
                pdf.addPage();
            }
        }
        pdf.save('stock_count.pdf');
        loading.current?.close();
    });
    const save = React.useCallback(async (status: number, filter: { group?: string[], sub_group?: string[] }, adjustment: string | undefined) => {
        let result: Array<StockData> = [];
        for (const i in left ?? []) {
            result = result.concat(left![i]);
        }
        for (const i in right ?? []) {
            result = result.concat(right![i]);
        }
        loading.current?.showModal();
        try {
            const response = await saveCountResult({
                lead: lead.current, result, status, orders, adjustment,
                id: id.current > 0 ? id.current : undefined, filter
            });
            loading.current?.close();
            if (typeof response === 'string') {
                Toast.show(response);
            } else {
                window.onbeforeunload = null;
                localStorage.removeItem('stock_count_left');
                localStorage.removeItem('stock_count_right');
                localStorage.removeItem('stock_count_info');
                Toast.show(response.msg);
                id.current = response.data;
                if (status) {
                    setSearch(new URLSearchParams([['reload', '1']]));
                }
                return id.current;
            }
        } catch (e: any) {
            loading.current?.close();
            e && Toast.show(e);
        }
        return 0;
    }, [setSearch, left, right, orders]);
    React.useEffect(() => {
        if (config.length === 0) {
            return;
        }
        if (search.has('id')) {
            const item = history.find(v => v.id.toString() === search.get('id'));
            if (item) {
                (async () => {
                    const result = (item.result ?? []) as StockData[];
                    let rack: Set<number> | Array<number> = new Set<number>();
                    const data: Record<number, Array<StockData>> = {};
                    lead.current = item.lead;
                    id.current = item.id;
                    const info: any = { ...item };
                    delete info.result;
                    localStorage.setItem('stock_count_info', JSON.stringify(info));
                    for (const item of result) {
                        rack.add(item.area);
                        data[item.area] ??= [];
                        data[item.area].push(item);
                    }
                    rack = Array.from(rack);
                    const left = await parseStockData(data[Math.min(...rack)] ?? []);
                    localStorage.setItem('stock_count_left', JSON.stringify(left));
                    setLeft(left);
                    if (rack.length === 2) {
                        const right = await parseStockData(data[Math.max(...rack)] ?? [])
                        localStorage.setItem('stock_count_right', JSON.stringify(right));
                        setRight(right);
                    }
                    setRack(Array.from(rack));
                })();
                return;
            }
        }
        if (props.lead) {
            const rack = search.getAll('rack[]').map(i => parseFloat(i));
            setRack(rack);
            lead.current = props.lead;
            id.current = 0;
            localStorage.setItem('stock_count_info', JSON.stringify({ lead: props.lead, rack }));
            load(rack[0], setLeft);
            rack.length === 2 && load(rack[1], setRight);
        }
    }, [config, setLeft, setRight, setRack, props.lead, search, history, load, parseStockData]);
    return (
        <Context.Provider value={[left, setLeft, right, setRight]}>
            <dialog ref={loading} className="loading">
                <FontAwesomeIcon icon={faSpinner} spin size="3x" />
            </dialog>
            <div className="stock-count h-100" style={{ gridTemplateColumns: 'repeat(' + rack.length + ',1fr)' }} ref={parent}>
                <MDBCard className="head" style={{ gridArea: '1 / 1 / 2 / ' + (rack.length + 1) }}>
                    <MDBCardBody className="d-flex gap-2">
                        <h2 className="h5 m-0 col align-self-center">{__('Stock Count')}</h2>
                        <MDBBtn onClick={setAsc.bind(null, !asc)}>{__(asc ? 'Ascended' : 'Descended')}</MDBBtn>
                        <MDBBtn onClick={setOrder.bind(null, true)}><span>{__('Prepared Orders')}</span>{orders.length > 0 ? (<MDBBadge light className="position-absolute">{orders.length}</MDBBadge>) : null}</MDBBtn>
                        <MDBBtn onClick={pdf.current}>{__('Export')} PDF</MDBBtn>
                        <MDBBtn onClick={setSearch.bind(null, undefined)}>{__('Change Racks')}</MDBBtn>
                        <MDBBtn onClick={save.bind(null, 0, {}, '')}>{__('Save Draft')}</MDBBtn>
                        <MDBBtn onClick={setComplete.bind(null, true)}>{__('Complete')}</MDBBtn>
                    </MDBCardBody>
                </MDBCard>
                {left ? columns[0]?.map((c, i) => (
                    <Rack key={rack[0] + '-' + c} className={i === 0 ? 'first' : ''} style={{ gridArea: (i + 2) + ' / 1 / ' + (i + 3) + ' / 2' }} columns={asc ? [c, c + 1, c + 2] : [c - 2, c - 1, c]} area={rack[0]} config={config[rack[0]]} left />
                )) : null}
                {rack.length === 2 && right ? columns[1]?.map((c, i) => (
                    <Rack key={rack[1] + '-' + c} className={i === 0 ? 'first' : ''} style={{ gridArea: (i + 2) + ' / 2 / ' + (i + 3) + ' / 3' }} columns={asc ? [c, c + 1, c + 2] : [c - 2, c - 1, c]} area={rack[1]} config={config[rack[1]]} />
                )) : null}
            </div>
            <OrderPicker open={order} dismiss={setOrder.bind(null, false)} />
            <Complete open={complete} save={save.bind(null, 1)} dismiss={setComplete.bind(null, false)} />
        </Context.Provider>
    );
}

function Rack(props: { className?: string, style?: React.CSSProperties, left?: boolean, columns: Array<number>, area: number, config: GridConfig }) {
    const { 0: left, 2: right } = React.useContext(Context);
    const data = React.useMemo(() => (props.left ? left : right) ?? {}, [props.left, left, right]);
    const columns = React.useMemo(() => {
        const result = [];
        for (const c of props.columns) {
            if (c < props.config.cols && c >= 0) {
                result.push(c);
            } else {
                break;
            }
        }
        return result;
    }, [props.columns, props.config]);
    const { 0: edit, 1: setEdit } = React.useState<number[]>([]);
    return (
        <div className={'partial ' + (props.className ?? '')} title={props.config.name} style={props.style}>
            <MDBTable striped hover small>
                <colgroup>
                    <col />
                    {columns.map(i => (<col key={'c' + i} width={(100 / columns.length) + '%'} />))}
                </colgroup>
                <MDBTableHead>
                    <tr>
                        <th style={{ width: 1 }} />
                        {columns.map(i => (<th key={'c' + i} className="text-center">{i + 1}</th>))}
                    </tr>
                </MDBTableHead>
                <MDBTableBody>
                    {Array(props.config.rows).fill(true).map((_, r) => {
                        const row = props.config.rows - r - 1;
                        return (
                            <tr key={'r' + row}>
                                <th>{row + 1}</th>
                                {columns.map(c => (
                                    <td key={row + '-' + c} onClick={setEdit.bind(null, [props.area, row, c])} style={{ height: (100 / props.config.rows) + '%' }}>
                                        <ul>
                                            {data[row + 'x' + c]?.map((item, i) => (
                                                <li key={item.sku + '##' + i}>
                                                    <span className={'sku' + (typeof item.newSku !== 'undefined' ? ' pass' : ' error')}>{item.newSku ?? item.sku}</span>
                                                    <span className={'qty' + (typeof item.diff !== 'undefined' ? ' pass' : ' error')}>x{(new BigNumber(item.qty)).plus(item.diff ?? 0).toNumber()}</span>
                                                </li>
                                            ))}
                                        </ul>
                                    </td>
                                ))}
                            </tr>
                        );
                    })}
                </MDBTableBody>
            </MDBTable>
            <Edit config={props.config} left={Boolean(props.left)} location={edit} dismiss={setEdit.bind(null, [])} />
        </div>
    );
}

function Edit(props: { config: GridConfig, left: boolean, location: number[], dismiss: () => void }) {
    const { 0: left, 1: setLeft, 2: right, 3: setRight } = React.useContext(Context);
    const { 0: items, 1: setItems } = React.useState<StockData[]>([]);
    const { 0: added, 1: setAdded } = React.useState<StockData[]>([]);
    const { 0: needPwd, 1: setNeedPwd } = React.useState(false);
    const setData = React.useMemo(() => props.left ? setLeft : setRight, [props.left, setLeft, setRight]);
    const { 0: __ } = useLocale();
    const { 0: sku, 1: setSKU } = React.useState<string[]>([]);
    const { 0: newSku, 1: setNewSku } = React.useState<Array<string | undefined>>([]);
    const { 0: qty, 1: setQty } = React.useState<string[]>([]);
    const { 0: newQty, 1: setNewQty } = React.useState<Array<number | undefined>>([]);
    const { 0: password, 1: setPassword } = React.useState('');
    const confirmable = React.useMemo(() => {
        for (let i = 0; i < sku.length; i++) {
            if (newSku[i] !== sku[i]) {
                return true;
            }
        }
        for (let i = 0; i < qty.length; i++) {
            if (newQty[i]?.toString() !== qty[i]) {
                return true;
            }
        }
        return false;
    }, [sku, newSku, newQty, qty]);
    const onChangeSKU = React.useCallback((i: number, e: React.ChangeEvent<HTMLInputElement>) => {
        setSKU(old => {
            const cloned = [...old];
            cloned[i] = e.target.value;
            return cloned;
        });
    }, [setSKU]);
    const confirmSKU = React.useCallback((i: number, e: React.MouseEvent<HTMLElement>) => {
        e.stopPropagation();
        e.preventDefault();
        if (sku[i] === '') {
            ((e.target as HTMLElement)?.previousElementSibling as HTMLInputElement)?.reportValidity?.();
            return;
        }
        setNewSku(old => {
            const cloned = [...old];
            if (sku[i] === '') {
                cloned.splice(i, 1);
            } else {
                cloned[i] = sku[i];
            }
            return cloned;
        });
    }, [setNewSku, sku]);
    const onChangeQty = React.useCallback((i: number, e: React.ChangeEvent<HTMLInputElement>) => {
        setQty(old => {
            const cloned = [...old];
            cloned[i] = e.target.value;
            return cloned;
        });
    }, [setQty]);
    const confirmQty = React.useCallback((i: number, e: React.MouseEvent<HTMLElement>) => {
        e.stopPropagation();
        e.preventDefault();
        if (qty[i] === '') {
            ((e.target as HTMLElement)?.previousElementSibling as HTMLInputElement)?.reportValidity?.();
            return;
        }
        setNewQty(old => {
            const cloned = [...old];
            cloned[i] = assertNumber(qty[i]);
            return cloned;
        });
    }, [setNewQty, qty]);
    const add = React.useCallback((e: React.MouseEvent<HTMLElement>) => {
        e.stopPropagation();
        e.preventDefault();
        setAdded(old => {
            const cloned = [...old];
            cloned.push({
                sku: '', qty: 0, area: props.location[0], row: props.location[1], col: props.location[2], status: 0
            });
            return cloned;
        });
        setSKU(old => {
            const cloned = [...old];
            cloned.push('');
            return cloned;
        });
        setQty(old => {
            const cloned = [...old];
            cloned.push('0');
            return cloned;
        });
    }, [setAdded, setSKU, setQty, props.location]);
    const onUnload = React.useCallback((e: BeforeUnloadEvent) => {
        return e.returnValue = __('You have unsaved data to submit.');
    }, [__]);
    const submit = React.useCallback(async () => {
        try {
            if (needPwd) {
                if (password !== (await getConfig('auth/tmp'))['auth/tmp']) {
                    throw '';
                }
            }
            setData(old => {
                const cloned = { ...old }, key = props.location[1] + 'x' + props.location[2];
                const items = [...(cloned[key] ?? []), ...added];
                for (let i = 0; i < sku.length; i++) {
                    items[i].newSku = sku[i];
                    items[i].diff = (new BigNumber(qty[i])).minus(items[i].qty).toNumber();
                }
                cloned[key] = [];
                for (const item of items) {
                    if (item.newSku !== '') {
                        cloned[key].push(item);
                    }
                }
                localStorage.setItem('stock_count_' + (props.left ? 'left' : 'right'), JSON.stringify(cloned));
                return cloned;
            });
            props.dismiss();
            window.onbeforeunload = onUnload;
        } catch {
            Toast.show(__('The password is incorrect. Failed to save the data.'));
        }
    }, [needPwd, password, qty, sku, props.location, props.dismiss, onUnload, setPassword, __, props.left, added, setData]);
    React.useEffect(() => {
        const items = ((props.left ? left : right) ?? {})?.[props.location[1] + 'x' + props.location[2]] ?? [], sku = [], qty = [], newSku = [], newQty = [];
        let pwd = true;
        for (const item of items) {
            pwd = pwd && typeof item.newSku !== 'undefined' && typeof item.diff !== 'undefined';
            newSku.push(item.newSku);
            sku.push(item.newSku ?? item.sku);
            const q = (new BigNumber(item.qty)).plus(item.diff ?? 0);
            newQty.push(typeof item.diff === 'undefined' ? undefined : q.toNumber());
            qty.push(q.toString());
        }
        setNewSku(newSku);
        setSKU(sku);
        setNewQty(newQty);
        setQty(qty);
        setItems(items);
        setAdded([]);
        setNeedPwd(items.length > 0 && pwd);
    }, [props.left, left, right, props.location, setNeedPwd, props.config, setSKU, setQty, setNewSku, setNewQty, setAdded, setItems]);
    return (
        <MDBModal open={props.location.length > 0} onClose={props.dismiss} tabIndex="-1">
            <MDBModalDialog size="lg" centered>
                <MDBModalContent>
                    <MDBModalHeader className="justify-content-center">
                        {(props.config.name ?? ('Rack ' + props.location[0])) + ': ' + (props.location[2] + 1) + 'x' + (props.location[1] + 1)}
                    </MDBModalHeader>
                    <MDBModalBody className="d-flex flex-column gap-3">
                        {items.concat(added).map((item, i) => {
                            const diff = (new BigNumber(qty[i] || 0)).minus(item?.qty || 0);
                            return (
                                <div key={item.sku + i} className="d-flex gap-2">
                                    <MDBInputGroup>
                                        <MDBInput label="SKU" required value={sku[i] ?? ''} onChange={onChangeSKU.bind(null, i)} />
                                        {newSku[i] === sku[i] ? null : (
                                            <MDBBtn onClick={confirmSKU.bind(null, i)}>{__('Confirm')}</MDBBtn>
                                        )}
                                    </MDBInputGroup>
                                    <MDBInputGroup>
                                        <MDBInput type="number" required label={__('Current Qty')} min="0" value={qty[i] ?? ''} onChange={onChangeQty.bind(null, i)} />
                                        <MDBInput wrapperClass="edit-diff" disabled value={(diff.gt(0) ? '+' : '') + diff.toNumber()} />
                                        {newQty[i]?.toString() === qty[i] ? null : (
                                            <MDBBtn onClick={confirmQty.bind(null, i)}>{__('Confirm')}</MDBBtn>
                                        )}
                                    </MDBInputGroup>
                                </div>
                            );
                        })}
                        {needPwd ? (
                            <MDBInput type="password" label={__('Password')} value={password} onChange={e => setPassword(e.target.value)} />
                        ) : null}
                    </MDBModalBody>
                    <MDBModalFooter>
                        <MDBBtn onClick={add}>{__('New')}</MDBBtn>
                        <div className="col" />
                        <MDBBtn color="secondary" onClick={props.dismiss}>{__('Cancel')}</MDBBtn>
                        <MDBBtn disabled={confirmable} onClick={submit}>{__('Confirm')}</MDBBtn>
                    </MDBModalFooter>
                </MDBModalContent>
            </MDBModalDialog>
        </MDBModal>
    );
}

function OrderPicker(props: { open: boolean, dismiss: () => void }) {
    const { 0: search, 1: setSearch } = React.useState('');
    const { 0: selected, 1: setSelected } = React.useContext(OrderContext);
    const { 0: options, 1: setOptions } = React.useState<string[]>([]);
    const { 0: __ } = useLocale();
    const onChange = React.useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        setSearch(e.target.value);
    }, [setSearch]);
    const onSelect = React.useCallback((option: string, e: React.MouseEvent<HTMLElement>) => {
        e.stopPropagation();
        e.preventDefault();
        setSelected(old => {
            const cloned = [...old], p = cloned.indexOf(option);
            if (p > -1) {
                cloned.splice(p, 1);
            } else {
                cloned.push(option);
            }
            return cloned;
        });
    }, [setSelected]);
    const debounce = React.useRef(0);
    const load = React.useCallback<(key: string) => Promise<null | string[]>>((key: string) => {
        if (Date.now() - debounce.current <= 600) {
            return Promise.resolve(null);
        }
        debounce.current = Date.now();
        return new Promise(async resolve => {
            try {
                const response = await getSalesOrder({ limit: 10, page: 1, filter: { order_id: { like: '%' + key + '%' } } });
                if (typeof response !== 'string') {
                    resolve(response.data.map(order => order.order_id));
                    return;
                }
            } catch { }
            resolve([]);
        });
    }, []);
    React.useEffect(() => {
        if (search.length > 2) {
            load(search).then(data => data !== null && setOptions(data));
        }
    }, [search, setOptions]);
    return (
        <MDBModal open={props.open} onClose={props.dismiss} tabIndex="-1">
            <MDBModalDialog size="lg" className="order-picker">
                <MDBModalContent>
                    <MDBModalHeader>{__('Prepared Orders')}</MDBModalHeader>
                    <MDBModalBody>
                        <div className="d-flex gap-5">
                            <div className="col search">
                                <h6>{__('Search Orders')}</h6>
                                <MDBInput type="search" label={__('Order ID')} value={search} onChange={onChange} tabIndex={1000} />
                                <ul className="mb-0 mt-3">
                                    {options.map((option, i) => (
                                        <li key={option} onClick={onSelect.bind(null, option)} tabIndex={1001 + i}>{option}</li>
                                    ))}
                                </ul>
                            </div>
                            <div className="col selected">
                                <h6>{__('Selected Orders')}</h6>
                                <ul className="selected p-0 m-0">
                                    {Array.from(selected).map(option => (
                                        <li key={option} className="d-flex justify-content-between">
                                            <span>{option}</span>
                                            <MDBBtn size="sm" onClick={onSelect.bind(null, option)}>
                                                <FontAwesomeIcon icon={faXmark} />
                                            </MDBBtn>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    </MDBModalBody>
                    <MDBModalFooter>
                        <MDBBtn onClick={props.dismiss}>{__('Confirm')}</MDBBtn>
                    </MDBModalFooter>
                </MDBModalContent>
            </MDBModalDialog>
        </MDBModal>
    );
}

function Complete(props: { save: (filter: { group?: string[], sub_group?: string[] }, sa?: string) => Promise<number>, open: boolean, dismiss: () => void }) {
    const { 0: open, 1: setOpen } = React.useState(false);
    const { 0: left, 2: right } = React.useContext(Context);
    const { 0: __ } = useLocale();
    const config = React.useContext(ConfigContext);
    const { 0: groups, 1: setGroups } = React.useState<Array<OptionType>>([]);
    const { 0: subGroups, 1: setSubGroups } = React.useState<Array<OptionType>>([]);
    const { 0: checkedGroup, 1: setCheckedGroup } = React.useState<Record<string, boolean>>({});
    const { 0: checkedSubGroup, 1: setCheckedSubGroup } = React.useState<Record<string, boolean>>({});
    const { 0: warehouses, 1: setWarehouses } = React.useState<Array<OptionType>>([]);
    const { 0: warehouse, 1: setWarehouse } = React.useState('');
    const { 0: orderIds } = React.useContext(OrderContext);
    const loadProductGroup = React.useCallback(async () => {
        let options: typeof groups = [];
        try {
            const key = 'pg';
            const cache = sessionStorage.getItem(key);
            if (cache) {
                options = JSON.parse(cache);
            } else {
                const response = await getProductGroups();
                if (typeof response !== 'string') {
                    response.sort();
                    for (const item of response) {
                        if (item) {
                            options.push({
                                value: item, label: item
                            });
                        }
                    }
                    sessionStorage.setItem(key, JSON.stringify(options));
                }
            }
        } catch { }
        setGroups(options);
    }, [setGroups]);
    const loadProductSubGroup = React.useCallback(async () => {
        let groups = [];
        for (const k in checkedGroup) {
            checkedGroup[k] && groups.push(k);
        }
        if (!groups.length) {
            setSubGroups([]);
        } else {
            let options: typeof subGroups = [];
            try {
                groups.sort();
                const key = 'psg_' + groups.join('_');
                const cache = sessionStorage.getItem(key);
                if (cache) {
                    options = JSON.parse(cache);
                } else {
                    const response = await getProductSubGroups(groups);
                    if (typeof response !== 'string') {
                        response.sort();
                        for (const item of response) {
                            if (item) {
                                options.push({
                                    value: item, label: item
                                });
                            }
                        }
                        sessionStorage.setItem(key, JSON.stringify(options));
                    }
                }
            } catch { }
            setSubGroups(options);
        }
    }, [setSubGroups, checkedGroup]);
    const onCheckGroup = React.useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        setCheckedGroup(old => {
            const cloned = { ...old };
            cloned[e.target.value] = e.target.checked;
            return cloned;
        });
    }, [setCheckedGroup]);
    const onCheckSubGroup = React.useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        setCheckedSubGroup(old => {
            const cloned = { ...old };
            cloned[e.target.value] = e.target.checked;
            return cloned;
        });
    }, [setCheckedSubGroup]);
    const sa = React.useRef('');
    const adjust = React.useCallback(async (data: Array<StockData>, filter: Array<string> | null, orders: Record<string, BigNumber>) => {
        if (!warehouse) return;
        const stat: Record<string, BigNumber> = {};
        for (const item of data) {
            if (filter !== null && item.newSku && !filter.includes(item.newSku)) {
                continue;
            }
            if (item.newSku && item.sku !== item.newSku) {
                item.sku && (stat[item.sku] = (new BigNumber(item.qty)).negated());
                stat[item.newSku] = (stat[item.newSku ?? item.sku] ?? new BigNumber(0)).plus(item.qty).plus(item.diff ?? 0);
            } else if (item.diff) {
                stat[item.newSku ?? item.sku] = (stat[item.newSku ?? item.sku] ?? new BigNumber(0)).plus(item.diff);
            }
        }
        const stock = Object.entries(stat).map(({ 0: sku, 1: qty }) => ({ sku, qty: qty.plus(orders[sku] ?? 0).toNumber() })).filter(item => item.sku && item.qty);
        if (stock.length === 0) return;
        const msg = await adjustStock({ warehouse, reason: 'Stock Count Extra', stock });
        if (typeof msg === 'string') {
            Toast.show(msg);
        } else {
            sa.current = msg.id;
            Toast.show(__('Data has been up-to-date.'));
        }
    }, [warehouse]);
    const update = React.useCallback(async (data: Array<StockData>, filter: Array<string> | null) => {
        const out: Array<[number, number, number, string, number]> = [], add: Record<string, { area: number, row: number, col: number, sku: Record<string, number> }> = {};
        for (const item of data) {
            if (filter !== null && item.newSku && !filter.includes(item.newSku)) {
                continue;
            }
            const key = item.area + ':' + item.row + ':' + item.col;
            if (item.newSku && item.newSku !== item.sku) {
                item.sku && out.push([item.area, item.row, item.col, item.sku, item.qty]);
                add[key] ??= {
                    area: item.area, row: item.row, col: item.col, sku: {}
                };
                add[key].sku[item.newSku] = (new BigNumber(add[key].sku[item.newSku] ?? 0)).plus(item.qty).plus(item.diff ?? 0).toNumber();
            } else if (item.diff! > 0) {
                add[key] ??= {
                    area: item.area, row: item.row, col: item.col, sku: {}
                };
                add[key].sku[item.sku] = (new BigNumber(add[key].sku[item.sku] ?? 0)).plus(item.diff ?? 0).toNumber();
            } else if (item.diff! < 0) {
                out.push([item.area, item.row, item.col, item.sku, Math.abs(item.diff ?? 0)]);
            }
        }
        let ps: Array<Promise<any>> = [];
        if (out.length) {
            ps.push(stockOut({ position: out }));
        }
        for (const k in add) {
            ps.push(stockIn(add[k]));
            if (ps.length === 5) {
                await Promise.all(ps);
                ps = [];
            }
        }
        ps.length > 0 && await Promise.all(ps);
    }, []);
    const generate = React.useCallback(async (e: React.MouseEvent<HTMLElement>) => {
        e.stopPropagation();
        e.preventDefault();
        const loading = document.querySelector<HTMLDialogElement>('dialog.loading');
        loading?.showModal();
        try {
            const data = [];
            let skus = [];
            for (const key in left) {
                for (const item of left[key]) {
                    if (typeof item.diff === 'undefined') {
                        continue;
                    }
                    data.push(item);
                    item.newSku && skus.push(item.newSku);
                }
            }
            for (const key in right) {
                for (const item of right[key]) {
                    if (typeof item.diff === 'undefined') {
                        continue;
                    }
                    data.push(item);
                    item.newSku && skus.push(item.newSku);
                }
            }
            const group = Object.keys(checkedGroup).filter(k => checkedGroup[k]);
            const sub_group = Object.keys(checkedSubGroup).filter(k => checkedSubGroup[k]);
            skus = await filterSku(skus, { group, sub_group });
            const orders = await getOrderedItems(orderIds);
            await adjust(data, group.length > 0 ? skus : null, orders);
            await update(data, group.length > 0 ? skus : null);
            const id = await props.save({ group, sub_group }, sa.current);
            await getReport(id, config, __, data, group.length > 0 ? skus : null, orders);
        } catch (msg: any) {
            typeof msg === 'string' && Toast.show(msg);
        }
        loading?.close();
    }, [props.save, orderIds, __, config, left, right, adjust, update, checkedGroup, checkedSubGroup]);
    React.useEffect(() => setOpen(props.open), [props.open, setOpen]);
    React.useEffect(() => void loadProductGroup(), [loadProductGroup]);
    React.useEffect(() => void loadProductSubGroup(), [loadProductSubGroup]);
    React.useEffect(() => {
        request('Warehouses').then(response => {
            setWarehouses(response?.Items?.map((item: any) => ({ value: item.WarehouseCode, label: item.WarehouseName })) ?? []);
            setWarehouse(response?.Items?.[0]?.WarehouseCode ?? '');
        }, () => { });
    }, [setWarehouses, setWarehouse]);
    return (
        <MDBModal open={open} onClose={props.dismiss} tabIndex="-1">
            <MDBModalDialog size="xl">
                <MDBModalContent>
                    <MDBModalHeader className="justify-content-center">
                        {__('Complete Stock Count')}
                    </MDBModalHeader>
                    <MDBModalBody className="d-flex">
                        <MDBCol size="4">
                            <h5 className="mb-3">{__('Current stock count has been completed')}</h5>
                            <h6 className="mb-2">{__('Product Group')}</h6>
                            <div className="options">
                                {groups.map((group, i) => (
                                    <label key={group.value + i} className="d-block">
                                        <input type="checkbox" name="product_group" value={group.value} checked={Boolean(checkedGroup[group.value])} onChange={onCheckGroup} />
                                        <span className="ms-2">{group.label}</span>
                                    </label>
                                ))}
                            </div>
                        </MDBCol>
                        <MDBCol size="4" className="px-4">
                            <h5 className="mb-3" />
                            <h6 className="mb-2">{__('Product Sub Group')}</h6>
                            <div className="options">
                                {subGroups.map((group, i) => (
                                    <label key={group.value + i} className="d-block">
                                        <input type="checkbox" name="product_sub_group" value={group.value} checked={Boolean(checkedSubGroup[group.value])} onChange={onCheckSubGroup} />
                                        <span className="ms-2">{group.label}</span>
                                    </label>
                                ))}
                            </div>
                            {warehouses.length > 1 ? (
                                <Select options={warehouses} label={__('Warehouse')} value={warehouse} onChange={setWarehouse} />
                            ) : null}
                        </MDBCol>
                        <MDBCol size="4">
                            <h5 className="mb-3">{__('Prepared Orders')}</h5>
                            <ul>
                                {orderIds.map(id => (<li key={id}>{id}</li>))}
                            </ul>
                        </MDBCol>
                    </MDBModalBody>
                    <MDBModalFooter>
                        <MDBBtn color="secondary" onClick={props.dismiss}>{__('Close')}</MDBBtn>
                        <MDBBtn onClick={generate}>{__('Complete Stock Count')}</MDBBtn>
                    </MDBModalFooter>
                </MDBModalContent>
            </MDBModalDialog>
        </MDBModal>
    );
}

async function filterSku(skus: string[], filter?: { group?: string[], sub_group?: string[] }) {
    if (filter?.group?.length) {
        const products = await getProducts({ filter: { group: filter.group, sku: skus }, limit: -1 });
        if (typeof products !== 'string') {
            skus = [];
            for (const item of products.data) {
                skus.push(item.sku);
            }
        }
    }
    if (filter?.sub_group?.length) {
        const products = await getProducts({ filter: { sub_group: filter.sub_group, sku: skus }, limit: -1 });
        if (typeof products !== 'string') {
            skus = [];
            for (const item of products.data) {
                skus.push(item.sku);
            }
        }
    }
    return skus;
}

async function getOrderedItems(orderIds: string[]) {
    const result: Record<string, BigNumber> = {};
    if (orderIds?.length) {
        try {
            const order = await getSalesOrder({ filter: { order_id: orderIds }, limit: -1 });
            const order_id = typeof order === 'string' ? [] : order.data.map(order => order.id);
            if (order_id.length) {
                const response = await getSalesOrderItem({ filter: { order_id }, limit: -1 });
                if (typeof response !== 'string') {
                    response.data.forEach(item => {
                        result[item.sku] ??= new BigNumber(0);
                        result[item.sku] = result[item.sku].plus(item.qty);
                    });
                }
            }
        } catch { }
    }
    return result;
}

function getID(id: number | string) {
    return 'SC-' + id.toString().padStart(8, '0');
}

async function getReport(id: number | string, config: GridConfig[], __: (src: string) => string, data: Array<StockData>, skus: Array<string> | null, orders: Record<string, BigNumber>) {
    const loading = document.querySelector<HTMLDialogElement>('dialog.loading');
    loading?.showModal();
    const file = new ExcelJS.Workbook();
    const name = 'StockCounts_' + format(new Date(), 'yyyy.MM.dd_HH.mm.ss');
    const sheet1 = file.addWorksheet(name);
    const sheet2 = file.addWorksheet('Stock Count Log');
    const sheet3 = file.addWorksheet('Handling Order Items');
    let row = sheet1.getRow(1);
    row.getCell(1).value = '*Stock Count Number';
    row.getCell(2).value = '*Product Code';
    row.getCell(3).value = '*Count Quantity';
    row = sheet2.getRow(1);
    row.getCell(1).value = __('Rack');
    row.getCell(2).value = __('Column');
    row.getCell(3).value = __('Row');
    row.getCell(4).value = 'SKU';
    row.getCell(5).value = __('Book Quantity');
    row.getCell(6).value = __('Count Quantity');
    row = sheet3.getRow(1);
    row.getCell(1).value = 'SKU';
    row.getCell(2).value = __('Ordered Qty');
    const stat: Record<string, BigNumber> = { ...orders };
    let r = 2;
    for (const item of data) {
        if (item.newSku && item.sku && item.sku !== item.newSku) {
            stat[item.sku] ??= new BigNumber(0);
            row = sheet2.getRow(r++);
            row.getCell(1).value = config[item.area].name || __('Rack') + ' ' + (assertNumber(item.area) + 1);
            row.getCell(2).value = assertNumber(item.col) + 1;
            row.getCell(3).value = assertNumber(item.row) + 1;
            row.getCell(4).value = item.sku;
            row.getCell(5).value = item.qty;
            row.getCell(6).value = 0;
        }
        const sku = item.newSku ?? item.sku;
        const qty = (new BigNumber(item.qty)).plus(item.diff ?? 0);
        stat[sku] = (stat[sku] ?? new BigNumber(0)).plus(qty);
        row = sheet2.getRow(r++);
        row.getCell(1).value = config[item.area].name || __('Rack') + ' ' + (assertNumber(item.area) + 1);
        row.getCell(2).value = assertNumber(item.col) + 1;
        row.getCell(3).value = assertNumber(item.row) + 1;
        row.getCell(4).value = sku;
        row.getCell(5).value = item.qty;
        row.getCell(6).value = qty.toNumber();
    }
    id = getID(id);
    r = 2;
    for (const sku in stat) {
        if (skus === null || skus.includes(sku)) {
            const row = sheet1.getRow(r++);
            row.getCell(1).value = id;
            row.getCell(2).value = sku;
            row.getCell(3).value = stat[sku].toNumber();
        }
    }
    r = 2;
    for (const sku in orders) {
        const row = sheet3.getRow(r++);
        row.getCell(1).value = sku;
        row.getCell(2).value = orders[sku].toNumber();
    }
    try {
        const buffer = await file.xlsx.writeBuffer();
        let a = document.createElement('a');
        a.download = name + '.xlsx';
        a.href = URL.createObjectURL(new Blob([buffer]));
        document.body.append(a);
        a.click();
        URL.revokeObjectURL(a.href);
        a.remove();
    } catch { }
    loading?.close();
}
